/** Company users (M02-01, M02-06): invite, change role, disable. */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Pencil, UserPlus } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import type { Official } from './OfficialsPage';
import { useAuth } from '../../lib/auth';
import { dateTime, shortDate } from '../../lib/format';
import { Alert, Button, Card, Field, Modal, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, inputClass } from '../../components/ui';

interface Member {
  membershipId: string;
  userId: string;
  email: string;
  name: string;
  roleId: string;
  roleName: string;
  status: 'ACTIVE' | 'DISABLED';
  userStatus: 'ACTIVE' | 'DISABLED';
  mfaEnabled: boolean;
  expiresAt: string | null;
  lastLoginAt: string | null;
  grants: string[];
  officialId: string | null;
}

export interface RoleRow {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  isSystem: boolean;
  level: number;
  _count: { memberships: number };
}

export function useRoles() {
  return useQuery({ queryKey: ['roles'], queryFn: () => api<RoleRow[]>('/roles') });
}

export function UsersPage(): JSX.Element {
  const { can, user } = useAuth();
  const members = useQuery({ queryKey: ['members'], queryFn: () => api<Member[]>('/users') });
  const roles = useRoles();
  const [dialog, setDialog] = useState<Member | 'new' | null>(null);
  const manage = can('users.manage');

  return (
    <>
      <PageHeader
        title="Users"
        description="Unlimited users. Everyone has their own account; HR, payroll and timekeeping roles also use an authenticator app."
        actions={manage && <Button onClick={() => setDialog('new')}><UserPlus aria-hidden="true" className="h-4 w-4" /> Invite user</Button>}
      />
      <Card>
        {members.isLoading && <Skeleton className="h-48 w-full" />}
        {members.data && (
          <TableWrap>
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800">
                <Th>Name</Th>
                <Th>Role</Th>
                <Th>Two-step</Th>
                <Th>Access</Th>
                <Th>Last sign-in</Th>
                <Th>{''}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {members.data.map((m) => (
                <tr key={m.membershipId} className={m.status === 'ACTIVE' ? '' : 'opacity-50'}>
                  <Td>
                    <span className="font-medium">{m.name}</span>
                    <span className="block text-xs text-slate-500">{m.email}</span>
                  </Td>
                  <Td className="whitespace-nowrap">{m.roleName}</Td>
                  <Td>{m.mfaEnabled ? <StatusChip tone="ok">On</StatusChip> : <StatusChip tone="warning">Not yet</StatusChip>}</Td>
                  <Td className="whitespace-nowrap">
                    {m.status === 'DISABLED' ? <StatusChip tone="neutral">Disabled</StatusChip> : m.expiresAt ? `Until ${shortDate(m.expiresAt)}` : 'Active'}
                  </Td>
                  <Td className="whitespace-nowrap text-slate-500">{dateTime(m.lastLoginAt)}</Td>
                  <Td align="right">
                    {manage && m.userId !== user?.id && m.roleName !== 'Owner' && (
                      <button className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" aria-label={`Edit ${m.name}`} onClick={() => setDialog(m)}>
                        <Pencil aria-hidden="true" className="h-4 w-4" />
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>
      {dialog && <MemberDialog member={dialog === 'new' ? null : dialog} roles={(roles.data ?? []).filter((r) => r.name !== 'Owner')} onClose={() => setDialog(null)} />}
    </>
  );
}

function MemberDialog({ member, roles, onClose }: { member: Member | null; roles: RoleRow[]; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [roleId, setRoleId] = useState(member?.roleId ?? roles.find((r) => r.name === 'Records Clerk')?.id ?? roles[0]?.id ?? '');
  const [expiresAt, setExpiresAt] = useState(member?.expiresAt?.slice(0, 10) ?? '');
  const [active, setActive] = useState(member ? member.status === 'ACTIVE' : true);
  const [officialId, setOfficialId] = useState<string>(member?.officialId ?? '');
  const { can } = useAuth();
  const officials = useQuery({ queryKey: ['officials', 'current'], queryFn: () => api<Official[]>('/barangay/officials?current=true'), enabled: can('officials.view') });
  const [error, setError] = useState<string | null>(null);
  const [temp, setTemp] = useState<string | null>(null);
  const isAuditor = roles.find((r) => r.id === roleId)?.name === 'Auditor';

  const save = useMutation({
    mutationFn: () => {
      const expiry = expiresAt ? new Date(`${expiresAt}T23:59:59+08:00`).toISOString() : null;
      const scope = { officialId: officialId || null };
      if (member) return api<unknown>(`/users/${member.membershipId}`, { method: 'PATCH', body: { roleId, status: active ? 'ACTIVE' : 'DISABLED', expiresAt: expiry, ...scope } });
      return api<{ temporaryPassword: string | null }>('/users', { method: 'POST', body: { email, name, roleId, expiresAt: expiry, ...scope } });
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['members'] });
      const t = (result as { temporaryPassword?: string | null } | undefined)?.temporaryPassword;
      if (t) setTemp(t);
      else onClose();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Could not save.'),
  });

  if (temp) {
    return (
      <Modal title="User invited" description="Give them this temporary password privately. They must change it at first sign-in; Officials, the Treasurer and administrators also set up two-step sign-in. It is not shown again." onClose={onClose}>
        <div className="flex items-center justify-between gap-2 rounded-xl bg-slate-100 px-4 py-3 font-mono text-lg dark:bg-slate-800">
          <span>{temp}</span>
          <Button size="sm" variant="secondary" onClick={() => void navigator.clipboard?.writeText(temp)}><Copy aria-hidden="true" className="h-4 w-4" /> Copy</Button>
        </div>
        <div className="mt-4 flex justify-end"><Button onClick={onClose}>Done</Button></div>
      </Modal>
    );
  }

  return (
    <Modal title={member ? `Edit ${member.name}` : 'Invite a user'} description={member ? member.email : 'If they already have an ITCSI Barangay sign-in, they keep it.'} onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {error && <Alert tone="critical">{error}</Alert>}
        {!member && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Full name" required><input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} autoFocus /></Field>
            <Field label="Email" required><input className={inputClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          </div>
        )}
        <Field label="Role" required hint={roles.find((r) => r.id === roleId)?.description}>
          <select className={inputClass} value={roleId} onChange={(e) => setRoleId(e.target.value)}>
            {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </Field>
        <Field label={isAuditor ? 'Access ends on' : 'Access ends on (optional)'} required={isAuditor} hint={isAuditor ? 'Auditor access is always time-boxed.' : undefined}>
          <input type="date" className={inputClass} value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
        </Field>
        {can('officials.view') && (
          <Field label="Official or staff record" hint="Links the login to the officials directory (signatory, Lupon member…).">
            <select className={inputClass} value={officialId} onChange={(e) => { setOfficialId(e.target.value); const o = officials.data?.find((x) => x.id === e.target.value); if (o && !member) { if (!name) setName(o.fullName); if (!email && o.email) setEmail(o.email); } }}>
              <option value="">Not linked</option>
              {officials.data?.map((o) => <option key={o.id} value={o.id}>{o.fullName} · {o.title}</option>)}
            </select>
          </Field>
        )}
        {member && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="h-4 w-4" checked={active} onChange={(e) => setActive(e.target.checked)} /> Access active
          </label>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" busy={save.isPending}>{member ? 'Save' : 'Invite'}</Button>
        </div>
      </form>
    </Modal>
  );
}
